// src/hooks/usePagination.ts
import { useState, useCallback } from 'react';

interface UsePaginationProps {
    initialPage?: number;
    initialLimit?: number;
}

interface PaginationResult {
    currentPage: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    setLimit: (limit: number) => void;
    setTotalItems: (total: number) => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    canGoNext: boolean;
    canGoPrevious: boolean;
    getPageInfo: () => {
        start: number;
        end: number;
        total: number;
    };
}

export const usePagination = ({
    initialPage = 1,
    initialLimit = 10
}: UsePaginationProps = {}): PaginationResult => {
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [limit, setLimit] = useState(initialLimit);
    const [totalItems, setTotalItems] = useState(0);

    const totalPages = Math.ceil(totalItems / limit);

    const goToFirstPage = useCallback(() => {
        setCurrentPage(1);
    }, []);

    const goToLastPage = useCallback(() => {
        setCurrentPage(totalPages);
    }, [totalPages]);

    const goToNextPage = useCallback(() => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, totalPages]);

    const goToPreviousPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }, [currentPage]);

    const canGoNext = currentPage < totalPages;
    const canGoPrevious = currentPage > 1;

    const getPageInfo = useCallback(() => {
        const start = (currentPage - 1) * limit + 1;
        const end = Math.min(currentPage * limit, totalItems);
        return { start, end, total: totalItems };
    }, [currentPage, limit, totalItems]);

    const handleSetCurrentPage = useCallback((page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const handleSetLimit = useCallback((newLimit: number) => {
        setLimit(newLimit);
        // Reset to first page when changing limit
        setCurrentPage(1);
    }, []);

    return {
        currentPage,
        limit,
        totalItems,
        totalPages,
        setCurrentPage: handleSetCurrentPage,
        setLimit: handleSetLimit,
        setTotalItems,
        goToFirstPage,
        goToLastPage,
        goToNextPage,
        goToPreviousPage,
        canGoNext,
        canGoPrevious,
        getPageInfo
    };
};

export default usePagination;